import {
  catchError,
  concatMap,
  distinctUntilChanged,
  EMPTY,
  filter,
  from,
  map,
  Observable,
  switchMap,
  withLatestFrom,
  of,
} from 'rxjs';
import { ofType } from 'redux-observable';
import { careCuesSlice } from '../careCues';
import { selectedVisitSlice, loadLatestNoteStatusFailed, loadLatestNoteStatusSucceeded } from './';
import { REMINDER_AVAILABILITY, REMINDER_STATUS } from '@ax/constants';
import { updateLynxEHRAppointment } from '@ax/service';
import { RootState, CareCuesInterface } from '../../';
import {
  selectAreCareCuesAvailable,
  selectAreCareCuesSeen,
  selectSelectedVisitUuid,
} from './selectors';
import { selectCareCues } from '../careCues/selectors';
import { latestNoteStatusAppointment } from '@ax/service';

export const onSetSelectedVisit = (action$: Observable<any>) =>
  action$.pipe(
    ofType(selectedVisitSlice.actions.setSelectedVisitDetails.type),
    switchMap((action) => {
      return from(
        latestNoteStatusAppointment({
          appointmentId: action.payload.uuid,
          noteId: action.payload.uuid,
        }),
      ).pipe(
        map((latestStatus) => {
          return loadLatestNoteStatusSucceeded({
            noteId: action.payload.uuid,
            status: latestStatus.status,
            createdAt: latestStatus.createdAt,
          });
        }),
        catchError((error) => {
          return of(
            loadLatestNoteStatusFailed({ error: error.message, noteId: action.payload.uuid }),
          );
        }),
      );
    }),
  );

const performUpdateReminderAvailabilityRequest = (noteId: string, reminderAvailability: string) => {
  return from(
    updateLynxEHRAppointment({
      appointmentId: noteId,
      reminderAvailability,
    }),
  ).pipe(
    map(() => {
      return selectedVisitSlice.actions.reminderAvailabilityUpdated({
        noteId,
        reminderAvailability,
      });
    }),
    catchError((error) => {
      return [
        selectedVisitSlice.actions.reminderAvailabilityUpdateFailed({
          noteId,
          reminderAvailability,
          message: 'There has been an error while updating the reminder availability.',
          error,
        }),
      ];
    }),
  );
};

export const onCareCuesSeenUpdateNoteReminderAvailability = (
  action$: Observable<any>,
  state$: Observable<RootState>,
) =>
  action$.pipe(
    ofType(careCuesSlice.actions.careCuesSeen.type),
    withLatestFrom(state$),
    filter(([action, state]) => {
      return (
        selectSelectedVisitUuid(state) === action.payload.noteId &&
        selectAreCareCuesAvailable(state)
      );
    }),
    switchMap(([action]) => {
      return performUpdateReminderAvailabilityRequest(
        action.payload.noteId,
        REMINDER_AVAILABILITY.SEEN,
      );
    }),
  );

export const onLoadCareCuesSuccessUpdateAvailabilityIfEmpty = (
  action$: Observable<any>,
  state$: Observable<RootState>,
) =>
  action$.pipe(
    ofType(careCuesSlice.actions.loadCareCuesSucceeded.type),
    withLatestFrom(state$),
    concatMap(([action, state]) => {
      const uuid = selectSelectedVisitUuid(state);
      const areCareCuesAvailable = selectAreCareCuesAvailable(state);
      const areCareCuesSeen = selectAreCareCuesSeen(state);
      const isSelectedVisit = action.payload.noteId === uuid;

      const hasPendingCareCues = action.payload.reminderGroups.some((group: CareCuesInterface) =>
        group.items.some((item) => [REMINDER_STATUS.UNSATISFIED].includes(item.status)),
      );

      if (isSelectedVisit && (areCareCuesAvailable || areCareCuesSeen) && !hasPendingCareCues) {
        return performUpdateReminderAvailabilityRequest(
          action.payload.noteId,
          REMINDER_AVAILABILITY.NO_DATA,
        );
      }

      return EMPTY;
    }),
  );

export const onCareCueItemDismissedUpdateAvailabilityIfEmpty = (
  action$: Observable<any>,
  state$: Observable<RootState>,
) =>
  action$.pipe(
    ofType(careCuesSlice.actions.dismissCareCueItemSucceeded.type),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const uuid = selectSelectedVisitUuid(state);
      const areCareCuesAvailable = selectAreCareCuesAvailable(state);
      const areCareCuesSeen = selectAreCareCuesSeen(state);
      const isSelectedVisit = action.payload.noteId === uuid;
      const careCues = selectCareCues(state);
      const hasPendingCareCues = careCues && careCues.length > 0;

      if (isSelectedVisit && (areCareCuesAvailable || areCareCuesSeen) && !hasPendingCareCues) {
        return performUpdateReminderAvailabilityRequest(
          action.payload.noteId,
          REMINDER_AVAILABILITY.NO_DATA,
        );
      }

      return EMPTY;
    }),
  );

export const reloadCareCuesWhenVisitChanges = (action$: Observable<any>) =>
  action$.pipe(
    ofType(selectedVisitSlice.actions.setSelectedVisitDetails),
    distinctUntilChanged((prev, curr) => prev.payload.uuid === curr.payload.uuid),
    filter(
      (action) =>
        action.payload.uuid &&
        action.payload.reminderAvailability &&
        action.payload.reminderAvailability !== REMINDER_AVAILABILITY.NO_DATA,
    ),
    map((action) => {
      return careCuesSlice.actions.loadCareCuesRequested({
        noteId: action.payload.uuid,
        isNavigation: true,
      });
    }),
  );

export const reloadCareCuesWhenReminderAvailabilityChangeToAvailable = (action$: Observable<any>) =>
  action$.pipe(
    ofType(selectedVisitSlice.actions.setSelectedVisitDetails),
    distinctUntilChanged(
      (prev, curr) => prev.payload.reminderAvailability === curr.payload.reminderAvailability,
    ),
    filter(
      (action) =>
        action.payload.uuid &&
        action.payload.reminderAvailability &&
        action.payload.reminderAvailability === REMINDER_AVAILABILITY.AVAILABLE,
    ),
    map((action) => {
      return careCuesSlice.actions.loadCareCuesRequested({
        noteId: action.payload.uuid,
        isNavigation: false,
      });
    }),
  );

export default [
  onCareCuesSeenUpdateNoteReminderAvailability,
  onLoadCareCuesSuccessUpdateAvailabilityIfEmpty,
  onCareCueItemDismissedUpdateAvailabilityIfEmpty,
  reloadCareCuesWhenVisitChanges,
  onSetSelectedVisit,
  reloadCareCuesWhenReminderAvailabilityChangeToAvailable,
];
